import { useEffect, useMemo, useRef, useState } from 'react'
import {
  AlertTriangle,
  Ban,
  Plus,
  X,
  Trash2,
  Columns3,
  Rows3,
  Grid3x3,
  Calculator,
  ListChecks,
  Upload,
  Pin,
  PinOff,
  Gauge,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog'
import { cn } from '@/lib/utils'
import {
  OOS_CELL_TYPES,
  cellKey,
  effectiveCellOos,
  isEmptyCellConfig,
  resolveCell,
  tableAxisRefIds,
} from '@/lib/tableForm'
import { DEPENDENT_SURFACE_LABELS, findCellDependents, type FieldDependent } from '@/lib/formSections'
import { cellOverridesStage, cellOverridesTestCode, cellStage, cellTestCode } from '@/lib/lis/fieldStage'
import {
  keepsLinkOnTypeChange,
  linkCell,
  linkedLibraryIds,
  type CellLibraryField,
} from '@/lib/tableCellLibrary'
import { SearchableSelect } from '@/components/ui/searchable-select'
import { CellFormulaBuilder } from './CellFormulaBuilder'
import { OosSpecBuilder } from './OosSpecBuilder'
import { LisStageSelect, LisTestCodeSelect, type LisStageTests } from './LisStageSelect'
import type { FormField } from './types'
import type { CrossFormAuthoring } from '@/hooks/useCrossFormAuthoring'
import type {
  TableCellConfig,
  TableCellType,
  TableColumn,
  TableColumnType,
  TableConfig,
  TableRow,
} from '@/types/cgt'

// Single label map for every table/cell type — used by BOTH the Columns
// section's column-default dropdown and the Cell types section's per-cell
// override dropdown, so the two never drift out of sync or disagree on what
// "Calculated" is called.
const TABLE_TYPE_LABELS: Record<TableColumnType, string> = {
  text: 'Text',
  number: 'Number',
  date: 'Date',
  datetime: 'Date & time',
  dropdown: 'Dropdown',
  checkbox: 'Checkbox',
  image: 'Image',
  description: 'Description',
  calculated: 'Calculated',
}

const TABLE_TYPES = Object.keys(TABLE_TYPE_LABELS) as TableColumnType[]

const INHERIT = '__inherit__'
/** The "no Field Library field" choice; an option needs a non-empty value. */
const NOT_LINKED = '__none__'

/** Cell types a lab result cannot be written into, so they take no LIS stage. */
const NO_STAGE_TYPES: ReadonlySet<TableCellType> = new Set<TableCellType>([
  'calculated',
  'image',
  'description',
])

/**
 * Minimal RFC-4180-ish CSV parser: handles quoted fields, escaped quotes (""),
 * and CRLF/LF line endings. Strips a leading BOM and drops fully-blank rows.
 */
function parseCsv(text: string): string[][] {
  const rows: string[][] = []
  let cur: string[] = []
  let field = ''
  let inQuotes = false
  const s = text.replace(/^\uFEFF/, '')

  for (let i = 0; i < s.length; i++) {
    const ch = s[i]
    if (inQuotes) {
      if (ch === '"') {
        if (s[i + 1] === '"') {
          field += '"'
          i++
        } else {
          inQuotes = false
        }
      } else {
        field += ch
      }
    } else if (ch === '"') {
      inQuotes = true
    } else if (ch === ',') {
      cur.push(field)
      field = ''
    } else if (ch === '\n') {
      cur.push(field)
      rows.push(cur)
      cur = []
      field = ''
    } else if (ch !== '\r') {
      field += ch
    }
  }
  cur.push(field)
  rows.push(cur)

  return rows.filter(r => r.some(c => c.trim() !== ''))
}

export function makeDefaultTableConfig(): TableConfig {
  return {
    rowHeaderLabel: 'Year',
    columns: [
      { id: crypto.randomUUID(), label: 'Weight', columnType: 'number' },
      { id: crypto.randomUUID(), label: 'Height', columnType: 'number' },
    ],
    rows: [
      { id: crypto.randomUUID(), label: '2023' },
      { id: crypto.randomUUID(), label: '2024' },
    ],
    allowAddRows: false,
  }
}

interface Props {
  config: TableConfig
  onChange: (next: TableConfig) => void
  /** Id of the table field being designed — lets a cell formula tell this table from the form's others. */
  tableFieldId?: string
  /** This form's fields — passed through to a calculated cell's formula editor. */
  allFields?: FormField[]
  /** Cross-form reference authoring — passed through to a calculated cell's formula editor. */
  crossForm?: CrossFormAuthoring
  currentFormName?: string
  /**
   * The tenant's active LIS stage names, for the column and cell stage pickers.
   * Passed in rather than fetched here so the designer renders without a query
   * client or tenant (it is drawn with `renderToStaticMarkup` in tests).
   */
  lisStages?: string[]
  /** The tenant's stages with their test codes, for the test-code pickers (z8nrz7g2wm). Passed in like `lisStages`. */
  lisStageTests?: LisStageTests[]
  /**
   * The Field Library fields a cell can BE, as a placed field is one: linking
   * gives the cell the field's type and stamps `libraryFieldId`, which is how
   * anything downstream (Split and Pool, by system name) finds it. Passed in
   * for the same reason as `lisStages`.
   */
  libraryFields?: CellLibraryField[]
}

/**
 * Designs a table-layout form: the columns capture data, the rows are the
 * left-hand headers (e.g. years), and the respondent fills one value per cell.
 */
export function TableConfigBuilder({
  config,
  onChange,
  tableFieldId,
  allFields,
  crossForm,
  currentFormName,
  lisStages = [],
  lisStageTests = [],
  libraryFields = [],
}: Props) {
  const [bulkRows, setBulkRows] = useState('')
  // Cell whose advanced config (formula / dropdown options / specification) is
  // open in a dialog. `spec` opens it on the specification.
  const [editing, setEditing] = useState<{ rowId: string; colId: string; spec?: boolean } | null>(null)
  // Column whose specification is open in a dialog (z8nrz7eq4h).
  const [specColumnId, setSpecColumnId] = useState<string | null>(null)
  // Right-click menu for a cell (anchor toggle), positioned at the cursor.
  const [cellMenu, setCellMenu] = useState<{ rowId: string; colId: string; x: number; y: number } | null>(
    null,
  )
  // CSV import dialog state.
  const [importOpen, setImportOpen] = useState(false)
  const [importText, setImportText] = useState('')
  const [importError, setImportError] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  // Hidden for a tenant with no stages, unless this table already carries one.
  const showStages =
    lisStages.length > 0 ||
    config.columns.some(c => !!c.lisStage) ||
    Object.values(config.cells ?? {}).some(cellOverridesStage)
  const showTests =
    lisStageTests.some(s => s.testCodes.length > 0) ||
    config.columns.some(c => !!c.lisTestCode) ||
    Object.values(config.cells ?? {}).some(cellOverridesTestCode)

  const patch = (p: Partial<TableConfig>) => onChange({ ...config, ...p })
  const showRowHeader = config.showRowHeader !== false

  // ── CSV import ───────────────────────────────────────────────────────────────
  // First row is the header: cell 0 becomes the row-header label, the rest become
  // columns (defaulted to Number). Each later row's first cell becomes a row label.
  // Replaces the current columns/rows and clears per-cell overrides (their IDs change).
  const applyImport = (text: string) => {
    const grid = parseCsv(text)
    if (grid.length < 2) {
      setImportError('Need a header row plus at least one data row.')
      return
    }
    const [header, ...dataRows] = grid
    const columnLabels = header.slice(1).map(s => s.trim())
    const columns: TableColumn[] = columnLabels
      .filter(Boolean)
      .map(label => ({ id: crypto.randomUUID(), label, columnType: 'number' as const }))
    if (columns.length === 0) {
      setImportError('No columns found — the first cell is the row header, so add at least one more.')
      return
    }
    const rows: TableRow[] = dataRows
      .map(r => (r[0] ?? '').trim())
      .filter(Boolean)
      .map(label => ({ id: crypto.randomUUID(), label }))
    if (rows.length === 0) {
      setImportError('No row labels found in the first column.')
      return
    }
    onChange({
      ...config,
      rowHeaderLabel: header[0]?.trim() || config.rowHeaderLabel,
      columns,
      rows,
      cells: {},
    })
    setImportOpen(false)
    setImportText('')
    setImportError(null)
  }

  const handleImportFile = (file: File | undefined) => {
    if (!file) return
    const reader = new FileReader()
    reader.onload = () => {
      const text = String(reader.result ?? '')
      setImportText(text)
      setImportError(null)
    }
    reader.readAsText(file)
  }

  // ── Per-cell overrides ───────────────────────────────────────────────────────
  const getCell = (rowId: string, colId: string): TableCellConfig =>
    config.cells?.[cellKey(rowId, colId)] ?? {}

  const setCell = (rowId: string, colId: string, next: TableCellConfig) => {
    const key = cellKey(rowId, colId)
    const cells = { ...(config.cells ?? {}) }
    // `isEmptyCellConfig` rather than a check written out here: this one lost
    // `constantText` and deleted a description cell's words as they were typed.
    if (isEmptyCellConfig(next)) delete cells[key]
    else cells[key] = next
    patch({ cells })
  }

  const setCellType = (rowId: string, colId: string, type: TableCellType | null) => {
    const prev = getCell(rowId, colId)
    const resolvedType = type ?? config.columns.find(c => c.id === colId)?.columnType
    setCell(rowId, colId, {
      ...prev,
      type: type ?? undefined,
      // A cell IS its library field; retyping it by hand to something the
      // field is not ends the link, as it would for a placed field.
      libraryFieldId: keepsLinkOnTypeChange(prev.libraryFieldId, resolvedType, libraryFields)
        ? prev.libraryFieldId
        : undefined,
      // Drop type-specific config that no longer applies.
      formula: type === 'calculated' ? prev.formula : undefined,
      displayFormat: type === 'calculated' ? prev.displayFormat : undefined,
      options: type === 'dropdown' ? prev.options : undefined,
      constantText: type === 'description' ? prev.constantText : undefined,
      // A specification is written for one type, so it goes with the old one.
      oos: resolvedType === (prev.type ?? config.columns.find(c => c.id === colId)?.columnType) ? prev.oos : undefined,
    })
  }

  const patchCell = (rowId: string, colId: string, p: Partial<TableCellConfig>) =>
    setCell(rowId, colId, { ...getCell(rowId, colId), ...p })

  // `undefined` drops the override so the cell follows its column again; `null`
  // is a real override meaning "any stage".
  const setCellStage = (rowId: string, colId: string, stage: string | null | undefined) => {
    const next = { ...getCell(rowId, colId) }
    if (stage === undefined) delete next.lisStage
    else next.lisStage = stage
    setCell(rowId, colId, next)
  }

  // Same convention as the stage: `undefined` follows the column, `null` is none.
  const setCellTestCode = (rowId: string, colId: string, code: string | null | undefined) => {
    const next = { ...getCell(rowId, colId) }
    if (code === undefined) delete next.lisTestCode
    else next.lisTestCode = code
    setCell(rowId, colId, next)
  }

  const toggleCellAnchored = (rowId: string, colId: string) =>
    patchCell(rowId, colId, { anchored: !getCell(rowId, colId).anchored })

  // Not applicable: the cell is shaded and takes no value. `undefined`, not
  // false, when cleared, so an untouched cell's override can be dropped.
  const toggleCellDisabled = (rowId: string, colId: string) =>
    patchCell(rowId, colId, { disabled: getCell(rowId, colId).disabled ? undefined : true })

  // Every formula on the form that reads any of `refIds`, with this table
  // taken from `tableConfig` (the live config, or the config a change would
  // leave) rather than the form's saved copy.
  const formulasReading = (tableConfig: TableConfig, refIds: string[]): FieldDependent[] => {
    const fields = (allFields ?? []).filter(f => f.id !== tableFieldId)
    const self = { id: tableFieldId ?? '', label: 'This table', type: 'table', tableConfig }
    return findCellDependents([{ id: '', title: '', fields: [self, ...fields] }], refIds)
  }

  // Formulas that read a not applicable cell directly. Totals skip such a cell
  // on their own; a direct reference would read a cell that never has a value,
  // and the server refuses it, so the author is told which formulas to fix.
  // (formulasReading reads allFields and tableFieldId, hence the deps.)
  const notApplicableDependents = useMemo(() => {
    const keys = config.rows.flatMap(r =>
      config.columns.filter(c => config.cells?.[cellKey(r.id, c.id)]?.disabled).map(c => cellKey(r.id, c.id)),
    )
    return keys.length === 0 ? [] : formulasReading(config, keys)
  }, [config, allFields, tableFieldId])

  // Removing a row or column breaks every formula that reads one of its cells
  // or its total. As with deleting a referenced field, removal with no such
  // formula is immediate; otherwise a dialog names them first, and removal is
  // never blocked.
  const [pendingRemoval, setPendingRemoval] = useState<{
    label: string
    next: TableConfig
    dependents: FieldDependent[]
  } | null>(null)
  const requestRemoval = (axis: 'row' | 'col', id: string, label: string, next: TableConfig) => {
    // Checked against the table as it would be, so the removed cells' own
    // formulas are not counted.
    const dependents = formulasReading(next, tableAxisRefIds(tableFieldId ?? '', config, axis, id))
    if (dependents.length === 0) onChange(next)
    else setPendingRemoval({ label, next, dependents })
  }

  // The config without a row or column, including its per-cell overrides.
  const withoutCells = (keep: (key: string) => boolean): TableConfig['cells'] =>
    config.cells && Object.fromEntries(Object.entries(config.cells).filter(([key]) => keep(key)))

  // Close the cell context menu on Escape.
  useEffect(() => {
    if (!cellMenu) return
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setCellMenu(null)
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [cellMenu])

  // ── Columns ────────────────────────────────────────────────────────────────
  const updateColumn = (id: string, p: Partial<TableColumn>) =>
    patch({ columns: config.columns.map(c => (c.id === id ? { ...c, ...p } : c)) })
  // A specification is written for one type. Changing a column's type drops
  // the column's spec, and the spec of every cell in it that follows the
  // column's type (a cell with its own type keeps its own spec).
  const changeColumnType = (id: string, columnType: TableColumnType) => {
    const cells = config.cells
      ? Object.fromEntries(
          Object.entries(config.cells).flatMap(([key, cell]) => {
            if (!key.endsWith(`__${id}`) || cell.type || !cell.oos) return [[key, cell]]
            const rest = { ...cell, oos: undefined }
            return isEmptyCellConfig(rest) ? [] : [[key, rest]]
          }),
        )
      : config.cells
    patch({
      columns: config.columns.map(c => (c.id === id ? { ...c, columnType, oos: undefined } : c)),
      cells,
    })
  }
  const addColumn = () =>
    patch({
      columns: [
        ...config.columns,
        { id: crypto.randomUUID(), label: '', columnType: 'text' },
      ],
    })
  const removeColumn = (id: string) => {
    const col = config.columns.find(c => c.id === id)
    requestRemoval('col', id, col?.label || 'this column', {
      ...config,
      columns: config.columns.filter(c => c.id !== id),
      cells: withoutCells(key => !key.endsWith(`__${id}`)),
    })
  }

  // ── Rows ───────────────────────────────────────────────────────────────────
  const updateRow = (id: string, label: string) =>
    patch({ rows: config.rows.map(r => (r.id === id ? { ...r, label } : r)) })
  const addRow = () =>
    patch({ rows: [...config.rows, { id: crypto.randomUUID(), label: '' }] })
  const removeRow = (id: string) => {
    const row = config.rows.find(r => r.id === id)
    requestRemoval('row', id, row?.label || 'this row', {
      ...config,
      rows: config.rows.filter(r => r.id !== id),
      cells: withoutCells(key => !key.startsWith(`${id}__`)),
    })
  }
  const addBulkRows = () => {
    const labels = bulkRows
      .split('\n')
      .map(s => s.trim())
      .filter(Boolean)
    if (labels.length === 0) return
    const newRows: TableRow[] = labels.map(label => ({ id: crypto.randomUUID(), label }))
    patch({ rows: [...config.rows, ...newRows] })
    setBulkRows('')
  }

  return (
    <div className='mx-auto max-w-4xl space-y-8 px-6 py-8'>
      {/* Toolbar */}
      <div className='flex items-center justify-end'>
        <Button variant='outline' size='sm' onClick={() => setImportOpen(true)}>
          <Upload className='mr-1.5 h-3.5 w-3.5' />
          Import CSV
        </Button>
      </div>

      {/* Columns */}
      <section className='rounded-xl border bg-card p-5'>
        <div className='mb-1 flex items-center gap-2'>
          <Columns3 className='h-4 w-4 text-muted-foreground' />
          <h3 className='text-sm font-semibold'>Columns</h3>
        </div>
        <p className='mb-4 text-xs text-muted-foreground'>
          What is captured for each row — e.g. Weight, Height, Age.
        </p>

        <div className='space-y-3'>
          {config.columns.map((col, i) => (
            <div key={col.id} className='space-y-2 rounded-lg border bg-muted/20 p-3'>
              <div className='flex items-center gap-2'>
                <Input
                  value={col.label}
                  onChange={e => updateColumn(col.id, { label: e.target.value })}
                  placeholder={`Column ${i + 1} label…`}
                  className='h-9'
                />
                <Select
                  value={col.columnType}
                  // A specification is written for one type, so it goes with the old one.
                  onValueChange={v => changeColumnType(col.id, v as TableColumnType)}
                >
                  <SelectTrigger className='h-9 w-36 shrink-0'>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TABLE_TYPES.map(t => (
                      <SelectItem key={t} value={t}>
                        {TABLE_TYPE_LABELS[t]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {showStages && !NO_STAGE_TYPES.has(col.columnType) && (
                  <LisStageSelect
                    value={col.lisStage ?? null}
                    stages={lisStages}
                    onChange={v => updateColumn(col.id, { lisStage: v ?? undefined })}
                    className='w-36 shrink-0'
                    aria-label={`Lab stage for ${col.label || `column ${i + 1}`}`}
                  />
                )}
                {showTests && !NO_STAGE_TYPES.has(col.columnType) && (
                  <LisTestCodeSelect
                    value={col.lisTestCode ?? null}
                    stages={lisStageTests}
                    stage={col.lisStage}
                    onChange={v => updateColumn(col.id, { lisTestCode: v ?? undefined })}
                    className='w-28 shrink-0'
                    aria-label={`Connect test code for ${col.label || `column ${i + 1}`}`}
                  />
                )}
                {OOS_CELL_TYPES.includes(col.columnType) && (
                  <Button
                    size='icon'
                    variant='ghost'
                    className={cn('h-9 w-9 shrink-0', col.oos ? 'text-primary' : 'text-muted-foreground')}
                    onClick={() => setSpecColumnId(col.id)}
                    aria-label={`Specification for ${col.label || `column ${i + 1}`}`}
                    title={col.oos ? 'Specification set' : 'Specification'}
                  >
                    <Gauge className='h-4 w-4' />
                  </Button>
                )}
                <Button
                  size='icon'
                  variant='ghost'
                  className='h-9 w-9 shrink-0 text-muted-foreground hover:text-destructive'
                  onClick={() => removeColumn(col.id)}
                  disabled={config.columns.length <= 1}
                  aria-label='Remove column'
                >
                  <Trash2 className='h-4 w-4' />
                </Button>
              </div>

              {col.columnType === 'dropdown' && (
                <DropdownOptionsEditor
                  options={col.options ?? []}
                  onChange={options => updateColumn(col.id, { options })}
                />
              )}
              {col.columnType === 'calculated' && (
                <p className='text-[11px] text-muted-foreground'>
                  Each row's formula is set individually below, under Cell types.
                </p>
              )}
            </div>
          ))}
        </div>

        <Button variant='outline' size='sm' className='mt-3' onClick={addColumn}>
          <Plus className='mr-1.5 h-3.5 w-3.5' />
          Add column
        </Button>
      </section>

      {/* Rows */}
      <section className='rounded-xl border bg-card p-5'>
        <div className='mb-1 flex items-center gap-2'>
          <Rows3 className='h-4 w-4 text-muted-foreground' />
          <h3 className='text-sm font-semibold'>Rows</h3>
        </div>
        <p className='mb-4 text-xs text-muted-foreground'>
          The left-hand headers — e.g. 2023, 2024, 2025.
        </p>

        {showRowHeader && (
          <div className='mb-4 max-w-xs space-y-1.5'>
            <Label>Row header label</Label>
            <Input
              value={config.rowHeaderLabel}
              onChange={e => patch({ rowHeaderLabel: e.target.value })}
              placeholder='e.g. Year'
              className='h-9'
            />
          </div>
        )}

        <div className='space-y-2'>
          {config.rows.map((row, i) => (
            <div key={row.id} className='flex items-center gap-2'>
              <span className='w-6 shrink-0 text-right text-xs text-muted-foreground'>
                {i + 1}
              </span>
              {showRowHeader ? (
                <Input
                  value={row.label}
                  onChange={e => updateRow(row.id, e.target.value)}
                  placeholder={`Row ${i + 1} label…`}
                  className='h-9 max-w-xs'
                />
              ) : (
                <span className='w-full max-w-xs text-sm text-muted-foreground'>Row {i + 1}</span>
              )}
              <Button
                size='icon'
                variant='ghost'
                className='h-9 w-9 shrink-0 text-muted-foreground hover:text-destructive'
                onClick={() => removeRow(row.id)}
                disabled={config.rows.length <= 1}
                aria-label='Remove row'
              >
                <X className='h-4 w-4' />
              </Button>
            </div>
          ))}
        </div>

        <Button variant='outline' size='sm' className='mt-3' onClick={addRow}>
          <Plus className='mr-1.5 h-3.5 w-3.5' />
          Add row
        </Button>

        <div className='mt-5 space-y-1.5 border-t pt-4'>
          <Label className='text-xs text-muted-foreground'>Bulk add (one label per line)</Label>
          <Textarea
            value={bulkRows}
            onChange={e => setBulkRows(e.target.value)}
            placeholder={'2025\n2026\n2027'}
            rows={3}
            className='max-w-xs resize-none font-mono text-sm'
          />
          <Button variant='outline' size='sm' onClick={addBulkRows} disabled={!bulkRows.trim()}>
            <Plus className='mr-1.5 h-3.5 w-3.5' />
            Add rows
          </Button>
        </div>

        <div className='mt-5 flex items-center justify-between border-t pt-4'>
          <div>
            <Label className='font-normal' htmlFor='allow-add-rows'>
              Allow respondents to add rows
            </Label>
            <p className='mt-0.5 text-xs text-muted-foreground'>
              Let the person filling the form append extra rows.
            </p>
          </div>
          <Switch
            id='allow-add-rows'
            checked={!!config.allowAddRows}
            onCheckedChange={v => patch({ allowAddRows: v })}
          />
        </div>

        {/* Absent = on: a table saved before these switches keeps both headers. */}
        <div className='mt-4 flex items-center justify-between'>
          <div>
            <Label className='font-normal' htmlFor='show-row-header'>
              Show row header column
            </Label>
            <p className='mt-0.5 text-xs text-muted-foreground'>
              The left-hand column with each row's label.
            </p>
          </div>
          <Switch
            id='show-row-header'
            checked={showRowHeader}
            onCheckedChange={v => patch({ showRowHeader: v })}
          />
        </div>
        <div className='mt-4 flex items-center justify-between'>
          <div>
            <Label className='font-normal' htmlFor='show-column-header'>
              Show column header row
            </Label>
            <p className='mt-0.5 text-xs text-muted-foreground'>
              The top row with each column's label.
            </p>
          </div>
          <Switch
            id='show-column-header'
            checked={config.showColumnHeader !== false}
            onCheckedChange={v => patch({ showColumnHeader: v })}
          />
        </div>
      </section>

      {/* Per-cell types */}
      <section className='rounded-xl border bg-card p-5'>
        <div className='mb-1 flex items-center gap-2'>
          <Grid3x3 className='h-4 w-4 text-muted-foreground' />
          <h3 className='text-sm font-semibold'>Cell types</h3>
        </div>
        <p className='mb-4 text-xs text-muted-foreground'>
          Each cell defaults to its column type. Override any cell here — including making it a{' '}
          <span className='font-medium'>calculated</span> cell. A{' '}
          <span className='font-medium'>description</span> cell takes its fixed text here too, one
          cell at a time. Right-click a cell to{' '}
          <span className='font-medium'>anchor</span> it to the procedure header or to mark it{' '}
          <span className='font-medium'>not applicable</span>.
          {showStages && (
            <>
              {' '}A cell follows its column&apos;s <span className='font-medium'>lab stage</span>{' '}
              unless you set its own here.
            </>
          )}
        </p>

        {config.columns.length === 0 || config.rows.length === 0 ? (
          <p className='py-6 text-center text-sm text-muted-foreground'>
            Add at least one column and one row first.
          </p>
        ) : (
          <div className='overflow-x-persistent rounded-lg border'>
            <table className='w-full border-collapse text-sm'>
              <thead>
                <tr>
                  <th className='border bg-table-header px-3 py-2 text-left font-semibold text-table-header-foreground'>
                    {config.rowHeaderLabel || ' '}
                  </th>
                  {config.columns.map(c => (
                    <th
                      key={c.id}
                      className='border bg-table-header px-3 py-2 text-left font-semibold text-table-header-foreground'
                    >
                      {c.label || 'Untitled'}
                      <span className='ml-1 text-[10px] font-normal text-table-header-foreground/80'>
                        ({TABLE_TYPE_LABELS[c.columnType]})
                      </span>
                      {c.oos && (
                        <Gauge
                          className='ml-1 inline h-3 w-3 align-[-1px]'
                          aria-label='Column has a specification'
                        />
                      )}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {config.rows.map(row => (
                  <tr key={row.id}>
                    <th className='border bg-muted/20 px-3 py-2 text-left font-medium'>
                      {row.label || 'Untitled'}
                    </th>
                    {config.columns.map(col => {
                      const override = getCell(row.id, col.id)
                      const resolved = resolveCell(config, row.id, col)
                      const cellSpec = effectiveCellOos(config, cellKey(row.id, col.id))
                      const openMenu = (e: React.MouseEvent) => {
                        e.preventDefault()
                        setCellMenu({ rowId: row.id, colId: col.id, x: e.clientX, y: e.clientY })
                      }
                      // A not applicable cell has no type, value or link to set.
                      if (resolved.disabled) {
                        return (
                          <td
                            key={col.id}
                            aria-disabled='true'
                            className='border bg-muted p-1.5 align-top'
                            onContextMenu={openMenu}
                            title='Right-click to make this cell applicable again'
                          >
                            <span className='flex h-7 items-center gap-1 text-xs text-muted-foreground'>
                              <Ban className='h-3.5 w-3.5 shrink-0' />
                              Not applicable
                            </span>
                          </td>
                        )
                      }
                      return (
                        <td
                          key={col.id}
                          className={cn(
                            'border p-1.5 align-top',
                            override.anchored && 'bg-blue-50/60',
                          )}
                          onContextMenu={openMenu}
                          title='Right-click to anchor this cell or mark it not applicable'
                        >
                          <div className='flex items-center gap-1'>
                            {override.anchored && (
                              <Pin
                                className='h-3.5 w-3.5 shrink-0 text-blue-500'
                                aria-label='Anchored'
                              />
                            )}
                            {cellSpec && (
                              <Gauge
                                className={cn(
                                  'h-3.5 w-3.5 shrink-0',
                                  override.oos ? 'text-primary' : 'text-muted-foreground',
                                )}
                                aria-label={override.oos ? 'Cell has its own specification' : 'Column specification applies'}
                              />
                            )}
                            <Select
                              value={override.type ?? INHERIT}
                              onValueChange={v =>
                                setCellType(row.id, col.id, v === INHERIT ? null : (v as TableCellType))
                              }
                            >
                              <SelectTrigger className='h-7 text-xs'>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value={INHERIT}>
                                  Default ({TABLE_TYPE_LABELS[col.columnType]})
                                </SelectItem>
                                {TABLE_TYPES.map(t => (
                                  <SelectItem key={t} value={t}>
                                    {TABLE_TYPE_LABELS[t]}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            {(resolved.type === 'calculated' ||
                              override.type === 'dropdown') && (
                              <Button
                                type='button'
                                size='icon'
                                variant='ghost'
                                className={cn(
                                  'h-7 w-7 shrink-0',
                                  resolved.type === 'calculated' && !resolved.formula
                                    ? 'text-amber-600'
                                    : 'text-muted-foreground',
                                )}
                                title={
                                  resolved.type === 'calculated' ? 'Edit formula' : 'Edit options'
                                }
                                onClick={() => setEditing({ rowId: row.id, colId: col.id })}
                              >
                                {resolved.type === 'calculated' ? (
                                  <Calculator className='h-3.5 w-3.5' />
                                ) : (
                                  <ListChecks className='h-3.5 w-3.5' />
                                )}
                              </Button>
                            )}
                            {OOS_CELL_TYPES.includes(resolved.type) && (
                              <Button
                                type='button'
                                size='icon'
                                variant='ghost'
                                className='h-7 w-7 shrink-0 text-muted-foreground'
                                title='Cell specification'
                                aria-label={`Specification for ${col.label || 'column'} · ${row.label || 'row'}`}
                                onClick={() => setEditing({ rowId: row.id, colId: col.id, spec: true })}
                              >
                                <Gauge className='h-3.5 w-3.5' />
                              </Button>
                            )}
                          </div>
                          {showStages && !NO_STAGE_TYPES.has(resolved.type) && (
                            <LisStageSelect
                              value={
                                cellOverridesStage(override) ? (override.lisStage ?? null) : undefined
                              }
                              stages={lisStages}
                              inheritLabel={`Column (${col.lisStage || 'any stage'})`}
                              onChange={v => setCellStage(row.id, col.id, v)}
                              className={cn(
                                'mt-1 h-7 text-xs',
                                cellOverridesStage(override) && 'border-blue-300',
                              )}
                              aria-label={`Lab stage for ${col.label || 'column'} · ${row.label || 'row'} (${cellStage(config, row.id, col) ?? 'any stage'})`}
                            />
                          )}
                          {showTests && !NO_STAGE_TYPES.has(resolved.type) && (
                            <LisTestCodeSelect
                              value={
                                cellOverridesTestCode(override) ? (override.lisTestCode ?? null) : undefined
                              }
                              stages={lisStageTests}
                              stage={cellStage(config, row.id, col)}
                              inheritLabel={`Column (${col.lisTestCode || 'none'})`}
                              onChange={v => setCellTestCode(row.id, col.id, v)}
                              className={cn(
                                'mt-1 h-7 text-xs',
                                cellOverridesTestCode(override) && 'border-blue-300',
                              )}
                              aria-label={`Connect test code for ${col.label || 'column'} · ${row.label || 'row'} (${cellTestCode(config, row.id, col) ?? 'none'})`}
                            />
                          )}
                          {(libraryFields.length > 0 || !!override.libraryFieldId) && (
                            <LibraryFieldSelect
                              value={override.libraryFieldId}
                              fields={libraryFields}
                              taken={linkedLibraryIds(config.cells, cellKey(row.id, col.id))}
                              onChange={id =>
                                setCell(
                                  row.id,
                                  col.id,
                                  linkCell(
                                    getCell(row.id, col.id),
                                    id ? libraryFields.find(f => f.id === id) : undefined,
                                  ),
                                )
                              }
                              title={`Field Library field for ${col.label || 'column'} · ${row.label || 'row'}`}
                            />
                          )}
                          {/* Inline, not behind a dialog: a description cell's
                              whole content is this text, so hiding it behind an
                              icon left the author with no visible way to write
                              it — and no way to see what a cell already said. */}
                          {resolved.type === 'description' && (
                            <Input
                              value={override.constantText ?? ''}
                              onChange={e =>
                                patchCell(row.id, col.id, { constantText: e.target.value })
                              }
                              placeholder='Fixed text for this cell…'
                              aria-label={`Text for ${col.label || 'column'} · ${row.label || 'row'}`}
                              className='mt-1 h-7 text-xs'
                            />
                          )}
                        </td>
                      )
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {notApplicableDependents.length > 0 && (
          <div className='mt-3 flex items-start gap-1.5 rounded-md border border-amber-300 bg-amber-50 px-2.5 py-2 text-xs text-amber-800'>
            <AlertTriangle className='mt-0.5 h-3.5 w-3.5 shrink-0' />
            <div>
              <p>
                These formulas use a cell marked not applicable, which never has a value. Remove
                the cell from each formula:
              </p>
              <ul className='mt-1 list-disc pl-4'>
                {notApplicableDependents.map(d => (
                  <li key={`${d.surface}-${d.id}`}>
                    {d.label} ({DEPENDENT_SURFACE_LABELS[d.surface]})
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}
      </section>

      {/* Advanced cell-config dialog (formula or dropdown options) */}
      <CellConfigDialog
        config={config}
        editing={editing}
        onClose={() => setEditing(null)}
        getCell={getCell}
        patchCell={patchCell}
        tableFieldId={tableFieldId}
        allFields={allFields}
        crossForm={crossForm}
        currentFormName={currentFormName}
      />

      {/* Remove row / column warning: opens only when formulas read it */}
      <Dialog open={pendingRemoval !== null} onOpenChange={open => !open && setPendingRemoval(null)}>
        <DialogContent className='sm:max-w-lg'>
          <DialogHeader>
            <DialogTitle>Remove {pendingRemoval?.label}?</DialogTitle>
            <DialogDescription>
              Removing it leaves the following with a broken reference that must be repaired by
              hand:
            </DialogDescription>
          </DialogHeader>
          <ul className='max-h-40 list-disc space-y-1 overflow-y-auto pl-5 text-sm'>
            {pendingRemoval?.dependents.map(d => (
              <li key={`${d.surface}-${d.id}`}>
                {d.label}{' '}
                <span className='text-muted-foreground'>({DEPENDENT_SURFACE_LABELS[d.surface]})</span>
              </li>
            ))}
          </ul>
          <DialogFooter className='gap-2'>
            <Button variant='outline' onClick={() => setPendingRemoval(null)}>
              Cancel
            </Button>
            <Button
              variant='destructive'
              onClick={() => {
                if (pendingRemoval) onChange(pendingRemoval.next)
                setPendingRemoval(null)
              }}
            >
              Remove
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Column specification dialog (z8nrz7eq4h) */}
      <ColumnSpecDialog
        column={config.columns.find(c => c.id === specColumnId)}
        onClose={() => setSpecColumnId(null)}
        onChange={oos => specColumnId && updateColumn(specColumnId, { oos })}
      />

      {/* CSV import dialog */}
      <Dialog
        open={importOpen}
        onOpenChange={open => {
          setImportOpen(open)
          if (!open) setImportError(null)
        }}
      >
        <DialogContent className='max-w-lg'>
          <DialogHeader>
            <DialogTitle>Import from CSV</DialogTitle>
            <DialogDescription>
              The first row is the header: its first cell becomes the row-header label and the
              rest become columns (defaulted to Number). Each later row’s first cell becomes a
              row. This replaces the current columns and rows.
            </DialogDescription>
          </DialogHeader>

          <div className='space-y-3'>
            <input
              ref={fileInputRef}
              type='file'
              accept='.csv,text/csv'
              className='hidden'
              onChange={e => {
                handleImportFile(e.target.files?.[0])
                e.target.value = ''
              }}
            />
            <Button variant='outline' size='sm' onClick={() => fileInputRef.current?.click()}>
              <Upload className='mr-1.5 h-3.5 w-3.5' />
              Choose .csv file
            </Button>

            <div className='space-y-1.5'>
              <Label className='text-xs text-muted-foreground'>…or paste CSV</Label>
              <Textarea
                value={importText}
                onChange={e => {
                  setImportText(e.target.value)
                  setImportError(null)
                }}
                placeholder={'Parameters,Pre-Selection,Loaded Product\nVolume (mL),446,260\nTNC/mL x10^6,250,214'}
                rows={8}
                className='resize-none font-mono text-xs'
              />
            </div>

            {importError && <p className='text-xs text-destructive'>{importError}</p>}
          </div>

          <DialogFooter>
            <Button variant='ghost' onClick={() => setImportOpen(false)}>
              Cancel
            </Button>
            <Button onClick={() => applyImport(importText)} disabled={!importText.trim()}>
              Import
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cell right-click menu: anchor / unanchor, not applicable */}
      {cellMenu && (
        <>
          {/* Click-away overlay */}
          <div className='fixed inset-0 z-40' onClick={() => setCellMenu(null)} />
          <div
            className='fixed z-50 min-w-44 overflow-hidden rounded-md border bg-popover py-1 text-sm shadow-md'
            style={{ top: cellMenu.y, left: cellMenu.x }}
          >
            <button
              type='button'
              className='flex w-full items-center gap-2 px-3 py-1.5 text-left hover:bg-accent'
              onClick={() => {
                toggleCellAnchored(cellMenu.rowId, cellMenu.colId)
                setCellMenu(null)
              }}
            >
              {getCell(cellMenu.rowId, cellMenu.colId).anchored ? (
                <>
                  <PinOff className='h-3.5 w-3.5 text-muted-foreground' />
                  Remove from procedure header
                </>
              ) : (
                <>
                  <Pin className='h-3.5 w-3.5 text-blue-500' />
                  Anchor to procedure header
                </>
              )}
            </button>
            <button
              type='button'
              className='flex w-full items-center gap-2 px-3 py-1.5 text-left hover:bg-accent'
              onClick={() => {
                toggleCellDisabled(cellMenu.rowId, cellMenu.colId)
                setCellMenu(null)
              }}
            >
              <Ban className='h-3.5 w-3.5 text-muted-foreground' />
              {getCell(cellMenu.rowId, cellMenu.colId).disabled ? 'Make applicable' : 'Not applicable'}
            </button>
          </div>
        </>
      )}
    </div>
  )
}

/**
 * Which Field Library field a cell is, if any — searchable, because the library
 * holds well over a thousand fields. Choosing one makes the cell that field
 * (its type, its options); "No Field Library field" unlinks it. A field already
 * linked to another cell of this table is left out: one field in two places is
 * ambiguous to everything that reads it.
 */
function LibraryFieldSelect({
  value,
  fields,
  taken,
  onChange,
  title,
}: {
  value: string | undefined
  fields: CellLibraryField[]
  taken: ReadonlySet<string>
  onChange: (libraryFieldId: string | undefined) => void
  title: string
}) {
  const options = [
    { value: NOT_LINKED, label: 'No Field Library field' },
    ...fields
      .filter(f => f.id === value || !taken.has(f.id))
      .map(f => ({ value: f.id, label: f.systemName ? `${f.label} · ${f.systemName}` : f.label })),
  ]
  // A link to a field the list does not offer (another tenant's, or removed)
  // still has to read as linked rather than blank.
  const known = !value || fields.some(f => f.id === value)
  return (
    <div className='mt-1' title={title}>
      <SearchableSelect
        value={value ?? NOT_LINKED}
        onChange={v => onChange(v === NOT_LINKED ? undefined : v)}
        options={options}
        placeholder='Field Library field…'
        buttonClassName={cn('h-7 text-xs', value && 'border-emerald-300')}
        panelWidth={320}
        selectedOption={known ? undefined : { value: value!, label: 'Linked field (not in the library list)' }}
      />
    </div>
  )
}

function CellConfigDialog({
  config,
  editing,
  onClose,
  getCell,
  patchCell,
  tableFieldId,
  allFields,
  crossForm,
  currentFormName,
}: {
  config: TableConfig
  editing: { rowId: string; colId: string; spec?: boolean } | null
  onClose: () => void
  getCell: (rowId: string, colId: string) => TableCellConfig
  patchCell: (rowId: string, colId: string, p: Partial<TableCellConfig>) => void
  tableFieldId?: string
  allFields?: FormField[]
  crossForm?: CrossFormAuthoring
  currentFormName?: string
}) {
  if (!editing) return null
  const { rowId, colId } = editing
  const col = config.columns.find(c => c.id === colId)
  const row = config.rows.find(r => r.id === rowId)
  if (!col || !row) return null

  const cell = getCell(rowId, colId)
  const resolved = resolveCell(config, rowId, col)
  const isSpec = !!editing.spec && OOS_CELL_TYPES.includes(resolved.type)
  const isCalc = !isSpec && resolved.type === 'calculated'
  const inheritsColumnSpec = !cell.oos && resolved.type === col.columnType && !!col.oos

  return (
    <Dialog open onOpenChange={open => !open && onClose()}>
      {/* The calculated-cell editor is the full formula editor (IF, functions,
          live preview), so it gets more width and scrolls inside the viewport. */}
      <DialogContent className={isCalc ? 'max-h-[90vh] max-w-xl overflow-y-auto' : 'max-w-md'}>
        <DialogHeader>
          <DialogTitle>{isSpec ? 'Cell specification' : isCalc ? 'Calculated cell' : 'Dropdown options'}</DialogTitle>
          <DialogDescription>
            {col.label || 'Column'} @ {row.label || 'Row'}
          </DialogDescription>
        </DialogHeader>

        {isSpec ? (
          <div className='space-y-2'>
            <p className='text-xs text-muted-foreground'>
              {inheritsColumnSpec
                ? 'This cell uses the column specification. Add one here to use a different one for this cell only.'
                : 'A specification here is used for this cell only, in place of the column specification.'}
            </p>
            <OosSpecBuilder
              spec={cell.oos}
              fieldType={resolved.type}
              options={resolved.options ?? []}
              idPrefix={`cell-oos-${rowId}-${colId}`}
              onChange={oos => patchCell(rowId, colId, { oos })}
            />
          </div>
        ) : isCalc ? (
          <CellFormulaBuilder
            config={config}
            rowId={rowId}
            colId={colId}
            value={cell}
            onChange={p => patchCell(rowId, colId, p)}
            tableFieldId={tableFieldId}
            allFields={allFields}
            crossForm={crossForm}
            currentFormName={currentFormName}
          />
        ) : (
          <DropdownOptionsEditor
            options={cell.options ?? []}
            onChange={options => patchCell(rowId, colId, { options })}
          />
        )}

        <DialogFooter>
          <Button onClick={onClose}>Done</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/** Hosts the specification editor for one column; its type is the column type. */
function ColumnSpecDialog({
  column,
  onClose,
  onChange,
}: {
  column: TableColumn | undefined
  onClose: () => void
  onChange: (next: TableColumn['oos']) => void
}) {
  if (!column) return null
  return (
    <Dialog open onOpenChange={open => !open && onClose()}>
      <DialogContent className='max-h-[90vh] max-w-md overflow-y-auto'>
        <DialogHeader>
          <DialogTitle>Column specification</DialogTitle>
          <DialogDescription>
            {column.label || 'Column'}. Used for every cell of this column that keeps the column type,
            and for rows added while the form is filled.
          </DialogDescription>
        </DialogHeader>
        <OosSpecBuilder
          spec={column.oos}
          fieldType={column.columnType}
          options={column.options ?? []}
          idPrefix={`column-oos-${column.id}`}
          onChange={onChange}
        />
        <DialogFooter>
          <Button onClick={onClose}>Done</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function DropdownOptionsEditor({
  options,
  onChange,
}: {
  options: string[]
  onChange: (next: string[]) => void
}) {
  return (
    <div className='space-y-1.5 pl-1'>
      <Label className='text-xs text-muted-foreground'>Dropdown options</Label>
      {options.map((opt, i) => (
        <div key={i} className='flex items-center gap-1.5'>
          <Input
            value={opt}
            onChange={e => onChange(options.map((o, j) => (j === i ? e.target.value : o)))}
            placeholder={`Option ${i + 1}`}
            className='h-8 max-w-xs text-sm'
          />
          <Button
            size='icon'
            variant='ghost'
            className='h-8 w-8 shrink-0 text-muted-foreground hover:text-destructive'
            onClick={() => onChange(options.filter((_, j) => j !== i))}
            aria-label='Remove option'
          >
            <X className='h-3.5 w-3.5' />
          </Button>
        </div>
      ))}
      <Button
        variant='outline'
        size='sm'
        className='h-7'
        onClick={() => onChange([...options, ''])}
      >
        <Plus className='mr-1 h-3 w-3' />
        Add option
      </Button>
    </div>
  )
}

